import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useFeature } from "~hooks/useFeature";
import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import {
  type DataAnalyticsTabValue,
  DataAnalyticsTemplate,
} from "~ui/templates/DataAnalyticsTemplate/DataAnalyticsTemplate";

import {
  ApprenticeshipTab,
  CandidateSearchTab,
  OrgGrowthTab,
  SkillsCertificationsTab,
  StudentCandidacyTab,
  TalentAlignmentTab,
  UserGrowthTab,
} from "./AnalyticsTabs";

const STORAGE_KEY = "dataAnalytics.selectedTab";

const ALL_TAB_VALUES: DataAnalyticsTabValue[] = [
  "apprenticeships",
  "candidateSearch",
  "orgMetrics",
  "skillsCertifications",
  "studentCandidacy",
  "talentAlignment",
  "userMetrics",
];

export const DataAnalyticsPage = () => {
  const showResumes = useFeature("showResumes");
  const userMetrics = useFeature("platformInsightsUserMetrics");
  const organizationMetrics = useFeature("platformInsightsOrganizationMetrics");
  const studentCandidacy = useFeature("platformInsightsStudentCandidacy");
  const apprenticeships = useFeature("platformInsightsApprenticeships");
  const candidateSearch = useFeature("platformInsightsCandidateSearch");
  const skillsCertifications = useFeature("platformInsightsSkillsCertifications");
  const talentAlignment = useFeature("platformInsightsTalentAlignment");

  const tabs = useMemo(() => {
    const list: {
      content: JSX.Element;
      label: string;
      value: DataAnalyticsTabValue;
    }[] = [];

    if (userMetrics) {
      list.push({ content: <UserGrowthTab />, label: "User Metrics", value: "userMetrics" });
    }
    if (organizationMetrics) {
      list.push({ content: <OrgGrowthTab />, label: "Organization Metrics", value: "orgMetrics" });
    }

    if (showResumes) {
      if (studentCandidacy) {
        list.push({
          content: <StudentCandidacyTab />,
          label: "Student Candidacy",
          value: "studentCandidacy",
        });
      }
      if (apprenticeships) {
        list.push({
          content: <ApprenticeshipTab />,
          label: "Apprenticeships",
          value: "apprenticeships",
        });
      }
      if (candidateSearch) {
        list.push({
          content: <CandidateSearchTab />,
          label: "Candidate Search",
          value: "candidateSearch",
        });
      }
      if (skillsCertifications) {
        list.push({
          content: <SkillsCertificationsTab />,
          label: "Skills & Certifications",
          value: "skillsCertifications",
        });
      }
      if (talentAlignment) {
        list.push({
          content: <TalentAlignmentTab />,
          label: "Talent Alignment",
          value: "talentAlignment",
        });
      }
    }

    return list;
  }, [
    apprenticeships,
    candidateSearch,
    organizationMetrics,
    showResumes,
    skillsCertifications,
    studentCandidacy,
    talentAlignment,
    userMetrics,
  ]);

  const [selectedTab, setSelectedTab] = useState<DataAnalyticsTabValue>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ALL_TAB_VALUES.includes(stored as DataAnalyticsTabValue)) {
      return stored as DataAnalyticsTabValue;
    }
    return "userMetrics";
  });

  useEffect(() => {
    if (tabs.length === 0) {
      return;
    }
    if (!tabs.some((t) => t.value === selectedTab)) {
      const next = tabs[0].value;
      setSelectedTab(next);
      localStorage.setItem(STORAGE_KEY, next);
    }
  }, [tabs, selectedTab]);

  const handleTabChange = (newValue: DataAnalyticsTabValue) => {
    setSelectedTab(newValue);
    localStorage.setItem(STORAGE_KEY, newValue);
  };

  if (tabs.length === 0) {
    return (
      <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
        <PageHeader title="Platform Insights" />
        <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
          No Platform Insights tabs are enabled for this environment.
        </Typography>
      </Box>
    );
  }

  return <DataAnalyticsTemplate onTabChange={handleTabChange} selectedTab={selectedTab} tabs={tabs} />;
};
